// src/components/leads/edit-team.jsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Spinner,
} from "react-bootstrap";
import { db } from "../../firebase";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../../Context/AuthContext";
import {
  SUBSCRIPTION_TYPE_OPTIONS,
  isExcludedSubscriptionType,
} from "./subscription-types";
import {
  formatCalendarDate,
  parseDateInputLocal,
  toDateInputValue,
} from "../../utils/calendarDate";

const EditTeamForm = ({ show, handleClose, team, refreshTeams, mode = "edit" }) => {
  // ─── Authentication ──────────────────────────────────────────────────
  const { currentUser, userInfo } = useAuth();
  
  // ─── Top‐Level Team Fields ──────────────────────────────────────────
  const [teamName, setTeamName] = useState(team.teamName || "");
  const [conference, setConference] = useState(team.conference || "");
  const [division, setDivision] = useState(team.division || "");
  const [status, setStatus] = useState(team.status || "Active");
  const [assignedStaff, setAssignedStaff] = useState(team.assignedStaff || "");
  const [lastContact, setLastContact] = useState(() =>
    toDateInputValue(team.lastContact)
  );
  const [savingTeam, setSavingTeam] = useState(false);

  const subscriptionSelectOptions = useMemo(() => {
    const current = String(division || "").trim();
    if (!current || SUBSCRIPTION_TYPE_OPTIONS.includes(current)) {
      return SUBSCRIPTION_TYPE_OPTIONS;
    }
    if (isExcludedSubscriptionType(current)) {
      return SUBSCRIPTION_TYPE_OPTIONS;
    }
    return [current, ...SUBSCRIPTION_TYPE_OPTIONS];
  }, [division]);

  const subscriptionSelectValue =
    SUBSCRIPTION_TYPE_OPTIONS.includes(division) ||
    (division && !isExcludedSubscriptionType(division))
      ? division
      : "";

  // ─── Contacts Subcollection ─────────────────────────────────────────
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Track which contact is currently in "transfer" mode
  const [transferringContact, setTransferringContact] = useState(null);
  const [selectedTransferTeamId, setSelectedTransferTeamId] = useState(null);
  
  // Track which contacts are in edit mode
  const [editingContacts, setEditingContacts] = useState(new Set());
  
  // Modal states for contact management
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [selectedContactForEdit, setSelectedContactForEdit] = useState(null);
  
  // Modal states for team management
  const [showTeamNotesModal, setShowTeamNotesModal] = useState(false);
  const [showContactManagementModal, setShowContactManagementModal] = useState(false);

  // A "blank" template for new contacts
  const blankContactTemplate = {
    id: null,               // null => not yet in Firestore
    name: "",
    title: "",
    phone: "",
    email: "",
    lastTouchpoint: "",     // store as "YYYY-MM-DD" for <input type="date">
    hierarchy: "Other",     // default to "Other"
    teamId: team.id,
    isNew: true,            // flag so we add() instead of update()
    tempId: Date.now() + Math.random(), // unique temporary ID for React keys
  };

  // ─── All Teams (for “Transfer To” inline dropdown) ─────────────────
  const [allTeams, setAllTeams] = useState([]);
  const fetchAllTeams = async () => {
    try {
      const snapshot = await db.collection("Teams").get();
      const arr = snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().teamName,
      }));
      setAllTeams(arr);
    } catch (err) {
      console.error("Error fetching all teams:", err);
    }
  };

  // ─── Contact Notes Subcollection ────────────────────────────────────
  const [showContactNotesPanel, setShowContactNotesPanel] = useState(false);
  const [selectedContactForNotes, setSelectedContactForNotes] = useState(null);
  const [contactNotes, setContactNotes] = useState([]);
  const [loadingContactNotes, setLoadingContactNotes] = useState(false);
  const [newContactNote, setNewContactNote] = useState("");
  const textareaRef = useRef(null);

  // Fetch notes for a specific contact
  const fetchContactNotes = async (contactId) => {
    if (!contactId) return;
    setLoadingContactNotes(true);
    try {
      const snapshot = await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .doc(contactId)
        .collection("Notes")
        .orderBy("createdAt", "desc")
        .get();

      const notesArr = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setContactNotes(notesArr);
    } catch (err) {
      console.error("Error fetching contact notes:", err);
      toast.error("Could not load contact notes");
    } finally {
      setLoadingContactNotes(false);
    }
  };

  // Add a note for the selected contact
  const handleAddContactNote = async () => {
    if (!selectedContactForNotes || !newContactNote.trim()) {
      toast.error("Cannot add an empty note");
      return;
    }
    try {
      await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .doc(selectedContactForNotes.id)
        .collection("Notes")
        .add({
          note: newContactNote.trim(),
          createdAt: new Date(),
          createdBy: userInfo?.firstName && userInfo?.lastName 
            ? `${userInfo.firstName} ${userInfo.lastName}` 
            : userInfo?.email || currentUser?.email || "Unknown User",
        });
      toast.success("Note added to contact");
      setNewContactNote("");
      fetchContactNotes(selectedContactForNotes.id);
    } catch (err) {
      console.error("Error adding contact note:", err);
      toast.error("Could not add contact note");
    }
  };

  // ─── Team Touchpoints & Notes ───────────────────────────────────────
  const [touchpoints, setTouchpoints] = useState([]);
  const [loadingTouchpoints, setLoadingTouchpoints] = useState(false);
  const [newTeamNote, setNewTeamNote] = useState("");
  const [allTeamContactNotes, setAllTeamContactNotes] = useState([]);
  const [loadingAllTeamContactNotes, setLoadingAllTeamContactNotes] =
    useState(false);

  const fetchTouchpoints = async () => {
    setLoadingTouchpoints(true);
    try {
      const snapshot = await db
        .collection("Teams")
        .doc(team.id)
        .collection("Touchpoints")
        .orderBy("createdAt", "desc")
        .get();
      const tpArr = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTouchpoints(tpArr);
    } catch (err) {
      console.error("Error fetching touchpoints:", err);
      toast.error("Could not load touchpoints");
    } finally {
      setLoadingTouchpoints(false);
    }
  };

  const fetchAllTeamContactNotes = async () => {
    setLoadingAllTeamContactNotes(true);
    try {
      const contactsSnap = await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .get();

      const merged = [];
      for (const contactDoc of contactsSnap.docs) {
        const contactName = contactDoc.data().name || "—";
        const notesSnap = await db
          .collection("Teams")
          .doc(team.id)
          .collection("Contacts")
          .doc(contactDoc.id)
          .collection("Notes")
          .orderBy("createdAt", "desc")
          .get();

        notesSnap.docs.forEach((noteDoc) => {
          merged.push({
            id: noteDoc.id,
            contactId: contactDoc.id,
            contactName,
            ...noteDoc.data(),
          });
        });
      }

      merged.sort((a, b) => {
        const ta =
          a.createdAt && typeof a.createdAt.toDate === "function"
            ? a.createdAt.toDate().getTime()
            : 0;
        const tb =
          b.createdAt && typeof b.createdAt.toDate === "function"
            ? b.createdAt.toDate().getTime()
            : 0;
        return tb - ta;
      });

      setAllTeamContactNotes(merged);
    } catch (err) {
      console.error("Error fetching team contact notes:", err);
      toast.error("Could not load contact notes for this team");
      setAllTeamContactNotes([]);
    } finally {
      setLoadingAllTeamContactNotes(false);
    }
  };

  const handleAddTouchpoint = async () => {
    if (!newTeamNote.trim()) {
      toast.error("Team note cannot be empty");
      return;
    }
    try {
      await db
        .collection("Teams")
        .doc(team.id)
        .collection("Touchpoints")
        .add({
          note: newTeamNote.trim(),
          createdAt: new Date(),
          createdBy: userInfo?.firstName && userInfo?.lastName 
            ? `${userInfo.firstName} ${userInfo.lastName}` 
            : userInfo?.email || currentUser?.email || "Unknown User",
        });
      toast.success("Team note added");
      setNewTeamNote("");
      fetchTouchpoints();
    } catch (err) {
      console.error("Error adding touchpoint:", err);
      toast.error("Could not add team note");
    }
  };

  // ─── Fetch Contacts for this Team ────────────────────────────────────
  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const snapshot = await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .get();

      const docs = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || "",
          title: data.title || "",
          phone: data.phone || "",
          email: data.email || "",
          lastTouchpoint: toDateInputValue(data.lastTouchpoint),
          hierarchy: data.hierarchy || "Other",
          teamId: data.teamId || team.id,
          isNew: false,
          tempId: doc.id, // use the real ID as tempId for existing contacts
        };
      });
      setContacts(docs);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      toast.error("Could not load contacts");
    } finally {
      setLoadingContacts(false);
    }
  };

  // ─── On Modal Open or Team Change ────────────────────────────────────
  useEffect(() => {
    if (show && team) {
      // Pre‐fill top‐level fields
      setTeamName(team.teamName || "");
      setConference(team.conference || "");
      setDivision(team.division || "");
      setStatus(team.status || "Active");
      setAssignedStaff(team.assignedStaff || "");
      setLastContact(toDateInputValue(team.lastContact));

      // Fetch subcollections
      fetchContacts();
      fetchTouchpoints();
      fetchAllTeamContactNotes();
      fetchAllTeams();
      setTransferringContact(null);
      setSelectedTransferTeamId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, team]);

  // ─── Focus textarea when contact notes panel opens ────────────────────
  useEffect(() => {
    if (showContactNotesPanel) {
      console.log('Contact notes panel opened, newContactNote state:', newContactNote);
    }
  }, [showContactNotesPanel]);

  // ─── Focus trap for contact notes panel ────────────────────────────────
  useEffect(() => {
    if (showContactNotesPanel) {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setShowContactNotesPanel(false);
          setSelectedContactForNotes(null);
          setContactNotes([]);
          setNewContactNote("");
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showContactNotesPanel]);

  // ─── Save Top‐Level Team Info ─────────────────────────────────────────
  const handleSaveTeam = async () => {
    if (!teamName.trim()) {
      toast.error("Team Name is required");
      return;
    }
    setSavingTeam(true);
    try {
      await db.collection("Teams").doc(team.id).update({
        teamName: teamName.trim(),
        conference: conference.trim(),
        division: division.trim(),
        status,
        assignedStaff: assignedStaff.trim(),
        lastContact: lastContact ? parseDateInputLocal(lastContact) : null,
      });
      toast.success("Team updated");
      refreshTeams();
    } catch (err) {
      console.error("Error updating team:", err);
      toast.error("Could not update team");
    } finally {
      setSavingTeam(false);
    }
  };

  // ─── Contact Operations ─────────────────────────────────────────────
  // 1) Add a blank new contact row
  const handleAddContact = () => {
    const newContact = { ...blankContactTemplate };
    setContacts((prev) => [...prev, newContact]);
    // Put the new contact in edit mode
    setEditingContacts(prev => new Set(prev).add(newContact.tempId));
  };

  // 2) Delete a contact: if isNew, just remove from local state; else delete from Firestore
  const handleDeleteContact = async (contact) => {
    if (contact.isNew) {
      setContacts((prev) =>
        prev.filter((c) => c !== contact)
      );
      return;
    }
    if (!window.confirm(`Delete contact "${contact.name}"?`)) {
      return;
    }
    try {
      await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .doc(contact.id)
        .delete();
      toast.success("Contact deleted");
      fetchContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      toast.error("Could not delete contact");
    }
  };

  // 3) Save or update a contact row
  const handleSaveContact = async (index) => {
    const contact = contacts[index];
    if (!contact.name.trim()) {
      toast.error("Contact name is required");
      return;
    }
    try {
      const data = {
        name: contact.name.trim(),
        title: contact.title.trim(),
        phone: contact.phone.trim(),
        email: contact.email.trim(),
        lastTouchpoint: contact.lastTouchpoint
          ? parseDateInputLocal(contact.lastTouchpoint)
          : null,
        hierarchy: contact.hierarchy,
        teamId: team.id,
        updatedAt: new Date(),
      };

      if (contact.isNew) {
        await db
          .collection("Teams")
          .doc(team.id)
          .collection("Contacts")
          .add({
            ...data,
            createdAt: new Date(),
          });
        toast.success("Contact added");
      } else {
        await db
          .collection("Teams")
          .doc(team.id)
          .collection("Contacts")
          .doc(contact.id)
          .update(data);
        toast.success("Contact updated");
      }
      // Exit edit mode for this contact
      setEditingContacts(prev => {
        const newSet = new Set(prev);
        newSet.delete(contact.tempId || contact.id);
        return newSet;
      });
      fetchContacts();
    } catch (err) {
      console.error("Error saving contact:", err);
      toast.error("Could not save contact");
    }
  };

  // 4) Transfer a contact to another team
  const handleConfirmTransfer = async () => {
    if (!transferringContact) {
      toast.error("No contact selected for transfer");
      return;
    }
    
    if (!selectedTransferTeamId || selectedTransferTeamId === team.id) {
      setTransferringContact(null);
      setSelectedTransferTeamId(null);
      return; // no change or canceled
    }

    if (!window.confirm(`Move "${transferringContact.name}" to another team?`)) {
      setTransferringContact(null);
      setSelectedTransferTeamId(null);
      return;
    }

    try {
      // Read existing contact data
      const docRef = db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .doc(transferringContact.id);
      const docSnap = await docRef.get();
      if (!docSnap.exists) {
        toast.error("Contact not found");
        setTransferringContact(null);
        setSelectedTransferTeamId(null);
        return;
      }
      const data = docSnap.data();

      // Create new contact under new team
      const newContactRef = await db
        .collection("Teams")
        .doc(selectedTransferTeamId)
        .collection("Contacts")
        .add({
          name: data.name,
          title: data.title,
          phone: data.phone,
          email: data.email,
          lastTouchpoint: data.lastTouchpoint || null,
          hierarchy: data.hierarchy || "Other",
          teamId: selectedTransferTeamId,
          createdAt: data.createdAt || new Date(),
          updatedAt: new Date(),
        });

      // Transfer all contact notes to the new team
      const notesSnapshot = await db
        .collection("Teams")
        .doc(team.id)
        .collection("Contacts")
        .doc(transferringContact.id)
        .collection("Notes")
        .get();

      // Batch write all notes to the new contact
      const batch = db.batch();
      notesSnapshot.docs.forEach((noteDoc) => {
        const noteData = noteDoc.data();
        const newNoteRef = db
          .collection("Teams")
          .doc(selectedTransferTeamId)
          .collection("Contacts")
          .doc(newContactRef.id)
          .collection("Notes")
          .doc();
        batch.set(newNoteRef, noteData);
      });
      
      if (notesSnapshot.docs.length > 0) {
        await batch.commit();
      }

      // Delete the old contact and all its notes
      await docRef.delete();
      toast.success(`Contact and ${notesSnapshot.docs.length} note(s) moved to new team`);
      setTransferringContact(null);
      setSelectedTransferTeamId(null);
      fetchContacts();
    } catch (err) {
      console.error("Error moving contact:", err);
      toast.error("Could not move contact");
      setTransferringContact(null);
      setSelectedTransferTeamId(null);
    }
  };

  // ─── Sort Contacts by Hierarchy ───────────────────────────────────────
  // We want: Primary → Secondary → Tertiary → Other
  const hierarchyOrder = ["Primary", "Secondary", "Tertiary", "Other"];
  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      const aIdx = hierarchyOrder.indexOf(a.hierarchy);
      const bIdx = hierarchyOrder.indexOf(b.hierarchy);
      return aIdx - bIdx;
    });
  }, [contacts]);

  // ─── JSX Output ───────────────────────────────────────────────────────
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl" 
        dialogClassName="modal-90w"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {mode === "teamNotes" && "Team Touchpoints & Notes"}
            {mode === "contactManagement" && "Manage Contacts"}
            {mode === "edit" && "Edit Team Information"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mode === "teamNotes" ? (
            <TeamNotesManager
              teamId={team.id}
              teamName={team.teamName}
              touchpoints={touchpoints}
              loadingTouchpoints={loadingTouchpoints}
              newTeamNote={newTeamNote}
              setNewTeamNote={setNewTeamNote}
              onAddNote={handleAddTouchpoint}
              onRefreshNotes={fetchTouchpoints}
              allContactNotes={allTeamContactNotes}
              loadingAllContactNotes={loadingAllTeamContactNotes}
            />
          ) : mode === "contactManagement" ? (
            <ContactManagementManager
              team={team}
              contacts={sortedContacts}
              loadingContacts={loadingContacts}
              transferringContact={transferringContact}
              selectedTransferTeamId={selectedTransferTeamId}
              allTeams={allTeams}
              onAddContact={() => setShowAddContactModal(true)}
              onEditContact={(contact) => {
                setSelectedContactForEdit(contact);
                setShowEditContactModal(true);
              }}
              onTransferContact={(contact) => {
                setTransferringContact(contact);
                setSelectedTransferTeamId(team.id);
              }}
              onTransferTeamChange={(teamId) => {
                setSelectedTransferTeamId(teamId);
              }}
              onConfirmTransfer={handleConfirmTransfer}
              onCancelTransfer={() => {
                setTransferringContact(null);
                setSelectedTransferTeamId(null);
              }}
              onDeleteContact={handleDeleteContact}
              onContactNotes={(contact) => {
                setSelectedContactForNotes(contact);
                setShowContactNotesPanel(true);
                fetchContactNotes(contact.id);
              }}
              onRefreshContacts={fetchContacts}
            />
          ) : (
          <Form>
            <h5>Team Info</h5>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="editTeamName">
                  <Form.Label>Team Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="editConference">
                  <Form.Label>Conference</Form.Label>
                  <Form.Control
                    type="text"
                    value={conference}
                    onChange={(e) => setConference(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="editSubscriptionType">
                  <Form.Label>Subscription Type</Form.Label>
                  <Form.Select
                    value={subscriptionSelectValue}
                    onChange={(e) => setDivision(e.target.value)}
                  >
                    <option value="">Select subscription type…</option>
                    {subscriptionSelectOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Form.Select>
                  {isExcludedSubscriptionType(division) && (
                    <Form.Text className="text-muted">
                      Saved value &quot;{division}&quot; is no longer offered.
                      Choose a subscription type above to update it.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="editStatus">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {!["Active", "Pending", "Inactive"].includes(status) && status ? (
                      <option value={status}>{status}</option>
                    ) : null}
                    <option value="Active">Active (Current Client)</option>
                    <option value="Pending">Pending (Prospective Client)</option>
                    <option value="Inactive">Inactive (Former Client/Dead End)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="editAssignedStaff">
                  <Form.Label>Assigned Staff</Form.Label>
                  <Form.Control
                    type="text"
                    value={assignedStaff}
                    onChange={(e) => setAssignedStaff(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="editLastContact">
                  <Form.Label>Last Contact</Form.Label>
                  <Form.Control
                    type="date"
                    value={lastContact}
                    onChange={(e) => setLastContact(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

              <div className="d-flex gap-2 justify-content-end">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
            <Button
              variant="success"
              onClick={handleSaveTeam}
              disabled={savingTeam}
            >
              {savingTeam ? "Saving…" : "Save Team Info"}
            </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* ─── Add Contact Modal ──────────────────────────────────────────── */}
      <Modal
        show={showAddContactModal}
        onHide={() => setShowAddContactModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddContactForm
            teamId={team.id}
            onSave={(newContact) => {
              setShowAddContactModal(false);
              fetchContacts(); // Refresh the contacts list
            }}
            onCancel={() => setShowAddContactModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* ─── Edit Contact Modal ──────────────────────────────────────────── */}
      <Modal
        show={showEditContactModal}
        onHide={() => setShowEditContactModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact: {selectedContactForEdit?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContactForEdit && (
            <EditContactForm
              contact={selectedContactForEdit}
              teamId={team.id}
              onSave={(updatedContact) => {
                setShowEditContactModal(false);
                setSelectedContactForEdit(null);
                fetchContacts(); // Refresh the contacts list
              }}
              onCancel={() => {
                setShowEditContactModal(false);
                setSelectedContactForEdit(null);
              }}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* ─── Contact Notes Modal ──────────────────────────────────────────── */}
      <Modal
        show={showContactNotesPanel}
        onHide={() => {
          setShowContactNotesPanel(false);
          setSelectedContactForNotes(null);
          setContactNotes([]);
          setNewContactNote("");
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Notes for: {selectedContactForNotes?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContactNotesManager
            contact={selectedContactForNotes}
            teamId={team.id}
            contactNotes={contactNotes}
            loadingContactNotes={loadingContactNotes}
            newContactNote={newContactNote}
            setNewContactNote={setNewContactNote}
            onAddNote={handleAddContactNote}
            onRefreshNotes={() => fetchContactNotes(selectedContactForNotes?.id)}
          />
        </Modal.Body>
      </Modal>

      {/* ─── Team Notes Modal ──────────────────────────────────────────────── */}
      <Modal
        show={showTeamNotesModal}
        onHide={() => {
          setShowTeamNotesModal(false);
          setNewTeamNote("");
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Team Touchpoints & Notes: {team.teamName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TeamNotesManager
            teamId={team.id}
            teamName={team.teamName}
            touchpoints={touchpoints}
            loadingTouchpoints={loadingTouchpoints}
            newTeamNote={newTeamNote}
            setNewTeamNote={setNewTeamNote}
            onAddNote={handleAddTouchpoint}
            onRefreshNotes={fetchTouchpoints}
            allContactNotes={allTeamContactNotes}
            loadingAllContactNotes={loadingAllTeamContactNotes}
          />
        </Modal.Body>
      </Modal>

      {/* ─── Contact Management Modal ──────────────────────────────────────── */}
      <Modal
        show={showContactManagementModal}
        onHide={() => {
          setShowContactManagementModal(false);
          setTransferringContact(null);
          setSelectedTransferTeamId(null);
        }}
        size="xl"
        dialogClassName="modal-90w"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Contacts: {team.teamName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ContactManagementManager
            team={team}
            contacts={sortedContacts}
            loadingContacts={loadingContacts}
            transferringContact={transferringContact}
            selectedTransferTeamId={selectedTransferTeamId}
            allTeams={allTeams}
            onAddContact={() => setShowAddContactModal(true)}
            onEditContact={(contact) => {
              setSelectedContactForEdit(contact);
              setShowEditContactModal(true);
            }}
            onTransferContact={(contact) => {
              setTransferringContact(contact);
              setSelectedTransferTeamId(team.id);
            }}
            onTransferTeamChange={(teamId) => {
              setSelectedTransferTeamId(teamId);
            }}
            onConfirmTransfer={handleConfirmTransfer}
            onCancelTransfer={() => {
              setTransferringContact(null);
              setSelectedTransferTeamId(null);
            }}
            onDeleteContact={handleDeleteContact}
            onContactNotes={(contact) => {
              setSelectedContactForNotes(contact);
              setShowContactNotesPanel(true);
              fetchContactNotes(contact.id);
            }}
            onRefreshContacts={fetchContacts}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};


export default EditTeamForm;

// ─── Add Contact Form Component ──────────────────────────────────────────
const AddContactForm = ({ teamId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    phone: '',
    email: '',
    lastTouchpoint: '',
    hierarchy: 'Other'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await db.collection('Teams').doc(teamId).collection('Contacts').add({
        name: formData.name.trim(),
        title: formData.title.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        lastTouchpoint: formData.lastTouchpoint ? parseDateInputLocal(formData.lastTouchpoint) : null,
        hierarchy: formData.hierarchy,
        teamId: teamId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      toast.success('Contact added successfully');
      onSave();
    } catch (err) {
      console.error('Error adding contact:', err);
      toast.error('Could not add contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="addName">
            <Form.Label>Name *</Form.Label>
                        <Form.Control
                          type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="addHierarchy">
            <Form.Label>Hierarchy</Form.Label>
            <Form.Select
              value={formData.hierarchy}
              onChange={(e) => setFormData(prev => ({ ...prev, hierarchy: e.target.value }))}
            >
              <option value="Primary">Primary</option>
              <option value="Secondary">Secondary</option>
              <option value="Tertiary">Tertiary</option>
              <option value="Other">Other</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="addTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="addPhone">
            <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="addEmail">
            <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="addLastTouchpoint">
            <Form.Label>Last Touchpoint</Form.Label>
                        <Form.Control
                          type="date"
              value={formData.lastTouchpoint}
              onChange={(e) => setFormData(prev => ({ ...prev, lastTouchpoint: e.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
                            </Button>
        <Button variant="success" type="submit" disabled={saving}>
          {saving ? 'Adding...' : 'Add Contact'}
                            </Button>
                          </div>
    </Form>
  );
};


// ─── Edit Contact Form Component ─────────────────────────────────────────
const EditContactForm = ({ contact, teamId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: contact.name || '',
    title: contact.title || '',
    phone: contact.phone || '',
    email: contact.email || '',
    lastTouchpoint: toDateInputValue(contact.lastTouchpoint),
    hierarchy: contact.hierarchy || 'Other'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await db.collection('Teams').doc(teamId).collection('Contacts').doc(contact.id).update({
        name: formData.name.trim(),
        title: formData.title.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        lastTouchpoint: formData.lastTouchpoint ? parseDateInputLocal(formData.lastTouchpoint) : null,
        hierarchy: formData.hierarchy,
        updatedAt: new Date(),
      });
      toast.success('Contact updated successfully');
      onSave();
    } catch (err) {
      console.error('Error updating contact:', err);
      toast.error('Could not update contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="editName">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="editHierarchy">
            <Form.Label>Hierarchy</Form.Label>
                                <Form.Select
              value={formData.hierarchy}
              onChange={(e) => setFormData(prev => ({ ...prev, hierarchy: e.target.value }))}
            >
              <option value="Primary">Primary</option>
              <option value="Secondary">Secondary</option>
              <option value="Tertiary">Tertiary</option>
              <option value="Other">Other</option>
                                </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="editTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="editPhone">
            <Form.Label>Phone</Form.Label>
            <Form.Control
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group controlId="editEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="editLastTouchpoint">
            <Form.Label>Last Touchpoint</Form.Label>
            <Form.Control
              type="date"
              value={formData.lastTouchpoint}
              onChange={(e) => setFormData(prev => ({ ...prev, lastTouchpoint: e.target.value }))}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2 justify-content-end">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="success" type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </Form>
  );
};

// ─── Contact Notes Manager Component ─────────────────────────────────────
const ContactNotesManager = ({ 
  contact,
  teamId, 
  contactNotes,
  loadingContactNotes, 
  newContactNote,
  setNewContactNote,
  onAddNote,
  onRefreshNotes 
}) => {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
  };

  const handleSaveEdit = async () => {
    if (!editingNoteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      await db
        .collection('Teams')
        .doc(teamId)
        .collection('Contacts')
        .doc(contact.id)
        .collection('Notes')
        .doc(editingNoteId)
        .update({
          note: editingNoteText.trim(),
          updatedAt: new Date(),
        });
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditingNoteText('');
      onRefreshNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      toast.error('Could not update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await db
        .collection('Teams')
        .doc(teamId)
        .collection('Contacts')
        .doc(contact.id)
        .collection('Notes')
        .doc(noteId)
        .delete();
      toast.success('Note deleted');
      onRefreshNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Could not delete note');
    }
  };

  return (
    <div>
      {/* Add New Note */}
      <Form.Group className="mb-3">
        <Form.Label>Add New Note</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
          placeholder={`Add a note for ${contact?.name}...`}
            value={newContactNote}
            onChange={(e) => setNewContactNote(e.target.value)}
          />
        </Form.Group>
                                <Button
                                  variant="primary"
                                  size="sm"
        onClick={onAddNote}
        className="mb-3"
        disabled={!newContactNote.trim()}
      >
          + Add Note
        </Button>

      {/* Notes List */}
            <div>
        {loadingContactNotes ? (
          <div className="text-center">
              <Spinner animation="border" size="sm" /> Loading notes…
            </div>
          ) : (
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Note</th>
                  <th>By</th>
                <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contactNotes.map((cn) => {
                const dateStr = cn.createdAt
                  ? moment(cn.createdAt.toDate()).format("MM/DD/YYYY")
                  : "";
                  return (
                    <tr key={cn.id}>
                      <td>{dateStr}</td>
                    <td>
                      {editingNoteId === cn.id ? (
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                        />
                      ) : (
                        cn.note
                      )}
                    </td>
                      <td>{cn.createdBy || "-"}</td>
                    <td>
                      {editingNoteId === cn.id ? (
                        <div className="d-flex gap-1">
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleSaveEdit}
                          >
                            Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                            onClick={() => {
                              setEditingNoteId(null);
                              setEditingNoteText('');
                            }}
                                >
                                  Cancel
                                </Button>
                        </div>
                            ) : (
                        <div className="d-flex gap-1">
                              <Button
                            variant="outline-success"
                                size="sm"
                            onClick={() => handleEditNote(cn)}
                              >
                            Edit
                              </Button>
                          <Button
                            variant="outline-danger"
                                size="sm"
                            onClick={() => handleDeleteNote(cn.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                      </td>
                    </tr>
                  );
                })}
                {contactNotes.length === 0 && (
                  <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No notes yet. Add one above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
        </div>
    </div>
  );
};

// ─── Team Notes Manager Component ─────────────────────────────────────────
const TeamNotesManager = ({ 
  teamId, 
  teamName, 
  touchpoints, 
  loadingTouchpoints, 
  newTeamNote, 
  setNewTeamNote, 
  onAddNote,
  onRefreshNotes,
  allContactNotes = [],
  loadingAllContactNotes = false,
}) => {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
  };

  const handleSaveEdit = async () => {
    if (!editingNoteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      await db
        .collection('Teams')
        .doc(teamId)
        .collection('Touchpoints')
        .doc(editingNoteId)
        .update({
          note: editingNoteText.trim(),
          updatedAt: new Date(),
        });
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditingNoteText('');
      onRefreshNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      toast.error('Could not update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await db
        .collection('Teams')
        .doc(teamId)
        .collection('Touchpoints')
        .doc(noteId)
        .delete();
      toast.success('Note deleted');
      onRefreshNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Could not delete note');
    }
  };

  return (
    <div>
      {/* Add New Team Note */}
      <Form.Group className="mb-3">
        <Form.Label>Add New Team Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
          placeholder={`Add a note or conversation entry for ${teamName}...`}
                value={newTeamNote}
                onChange={(e) => setNewTeamNote(e.target.value)}
              />
            </Form.Group>
            <Button
        variant="primary" 
              size="sm"
        onClick={onAddNote}
        className="mb-3"
        disabled={!newTeamNote.trim()}
            >
              + Add Note
            </Button>

      {/* Team Notes List */}
                <div>
        {loadingTouchpoints ? (
          <div className="text-center">
                  <Spinner animation="border" size="sm" /> Loading notes…
                </div>
              ) : (
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Note</th>
                      <th>By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {touchpoints.map((tp) => {
                const dateStr = tp.createdAt
                  ? moment(tp.createdAt.toDate()).format("MM/DD/YYYY")
                  : "";
                      return (
                        <tr key={tp.id}>
                          <td>{dateStr}</td>
                          <td>
                            {editingNoteId === tp.id ? (
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                              />
                            ) : (
                              tp.note
                            )}
                          </td>
                          <td>{tp.createdBy || "-"}</td>
                          <td>
                            {editingNoteId === tp.id ? (
                              <div className="d-flex gap-1">
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={handleSaveEdit}
                                >
                                  Save
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => {
                                    setEditingNoteId(null);
                                    setEditingNoteText('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleEditNote(tp)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteNote(tp.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {touchpoints.length === 0 && (
                      <tr>
                  <td colSpan={4} className="text-center text-muted">
                    No team notes yet. Add one above.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>

      {!loadingAllContactNotes && allContactNotes.length > 0 && (
        <>
          <h6 className="mt-4 mb-2">Contact Notes</h6>
          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0 w-100">
              <thead>
                <tr>
                  <th className="text-nowrap" style={{ width: "1%" }}>
                    Date
                  </th>
                  <th className="text-nowrap" style={{ width: "1%" }}>
                    Contact
                  </th>
                  <th>Note</th>
                  <th className="text-nowrap" style={{ width: "1%" }}>
                    By
                  </th>
                </tr>
              </thead>
              <tbody>
                {allContactNotes.map((cn) => {
                  const dateStr =
                    cn.createdAt && typeof cn.createdAt.toDate === "function"
                      ? moment(cn.createdAt.toDate()).format("MM/DD/YYYY")
                      : "";
                  return (
                    <tr key={`${cn.contactId}-${cn.id}`}>
                      <td
                        className="text-nowrap align-top"
                        style={{ width: "1%" }}
                      >
                        {dateStr}
                      </td>
                      <td
                        className="text-nowrap align-top"
                        style={{ width: "1%" }}
                      >
                        {cn.contactName}
                      </td>
                      <td
                        className="align-top"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {cn.note}
                      </td>
                      <td
                        className="text-nowrap align-top"
                        style={{ width: "1%" }}
                      >
                        {cn.createdBy || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Contact Management Manager Component ─────────────────────────────────
const ContactManagementManager = ({ 
  team,
  contacts, 
  loadingContacts, 
  transferringContact,
  selectedTransferTeamId,
  allTeams,
  onAddContact,
  onEditContact,
  onTransferContact,
  onTransferTeamChange,
  onConfirmTransfer,
  onCancelTransfer,
  onDeleteContact,
  onContactNotes,
  onRefreshContacts
}) => {
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Contacts</h5>
        <Button
          variant="primary"
          size="sm"
          onClick={onAddContact}
        >
          + Add Contact
        </Button>
      </div>

      {loadingContacts ? (
        <div className="mt-3 text-center">
          <Spinner animation="border" size="sm" /> Loading contacts…
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <p>No contacts yet. Click "+ Add Contact" to create one.</p>
            </div>
          ) : (
        <Table bordered hover size="sm" className="mt-2">
              <thead>
                <tr>
              <th>Hierarchy</th>
              <th>Name</th>
              <th>Title</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Last Touchpoint</th>
              <th>Actions</th>
                </tr>
              </thead>
              <tbody>
            {contacts.map((contact, idx) => (
              <tr key={contact.tempId || contact.id || `contact-${idx}`}>
                {/* Hierarchy */}
                <td>
                  <span className={`badge ${
                    contact.hierarchy === 'Primary' ? 'bg-primary' :
                    contact.hierarchy === 'Secondary' ? 'bg-secondary' :
                    contact.hierarchy === 'Tertiary' ? 'bg-info' :
                    'bg-light text-dark'
                  }`}>
                    {contact.hierarchy}
                  </span>
                </td>

                {/* Name */}
                <td>
                  <strong>{contact.name}</strong>
                </td>

                {/* Title */}
                <td>{contact.title}</td>

                {/* Phone */}
                <td>{contact.phone}</td>

                {/* Email */}
                <td>{contact.email || "—"}</td>

                {/* Last Touchpoint */}
                <td>
                  {formatCalendarDate(contact.lastTouchpoint)}
                </td>

                {/* Actions */}
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => onEditContact(contact)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => onContactNotes(contact)}
                    >
                      Notes
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onTransferContact(contact)}
                    >
                      Transfer
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => onDeleteContact(contact)}
                    >
                      Delete
                    </Button>
                  </div>
                    </td>
                  </tr>
            ))}
              </tbody>
            </Table>
          )}

      {/* Transfer Contact Section */}
      {transferringContact && (
        <div className="mt-3 p-3 border rounded bg-light">
          <h6>Transfer Contact: {transferringContact.name}</h6>
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              size="sm"
              value={selectedTransferTeamId || team.id}
              onChange={(e) => {
                onTransferTeamChange(e.target.value);
              }}
              style={{ maxWidth: "200px" }}
            >
              <option value={team.id}>
                {team.teamName} (Current)
              </option>
              {allTeams
                .filter((t) => t.id !== team.id)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Form.Select>
            <Button
              variant="primary"
              size="sm"
              onClick={onConfirmTransfer}
            >
              Confirm Transfer
        </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onCancelTransfer}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};



